"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FilterableDataService = void 0;
const app_1 = require("app");
const search_1 = require("src/search");
const taxonomy_1 = require("src/taxonomy");
const FilterableList_1 = require("./FilterableList");
class FilterableDataService {
    get dictionary() {
        return this.config.dictionary;
    }
    constructor(config) {
        this.createZeroResultStatus = () => ({
            statusCode: FilterableList_1.ListStatusCode.zeroResults,
        });
        this.createSuccessStatus = () => ({
            statusCode: FilterableList_1.ListStatusCode.success,
        });
        this.getValueFromDictionary = (propName) => {
            return this.config.dictionary ? this.config.dictionary[propName] || '' : '';
        };
        this.isCommunicationError = (error) => {
            return error && (error.name === search_1.SEARCH_API_ERROR || error.name === taxonomy_1.TAXONOMY_API_ERROR);
        };
        this.createCommunicationErrorStatus = () => ({
            message: this.dictionary ? this.dictionary.communicationErrorMessage : '',
            statusCode: FilterableList_1.ListStatusCode.communicationError,
        });
        this.config = config;
        // Init taxonomy api with session storage
        this.taxonomyApi = new taxonomy_1.TaxonomyApi({
            storage: sessionStorage,
            storageKey: `TAXONOMY_${this.config.culture}_${config.type}`,
            url: this.config.taxonomyTranslationsUrl,
            waitForInProgress: true,
        });
    }
    getData(filters, pageProps, sort, pageSize, query, isInitialSearch, recurrentCall = false) {
        const uiParams = this.combineUIParameters(filters, pageProps.pageNumber, pageProps.pageid, sort, pageSize, query, isInitialSearch);
        // Prepare params for search API
        const searchApiParams = this.buildSearchApiParams(uiParams);
        // Prepare params for taxonomy API
        const taxonomyParams = this.buildTaxonomyApiParams(pageProps.pageid);
        // Load data from search and taxonomy API
        return Promise.all([this.loadData(searchApiParams), this.loadTaxonomy(taxonomyParams)])
            .then(([data, taxonomies]) => {
            return this.loadSpecificTaxonomies(data, taxonomies);
        })
            .then(([data, taxonomies]) => {
            this.taxonomyApiResult = taxonomies;
            return Object.assign(Object.assign({}, this.processSuccessResult(data, taxonomies, searchApiParams)), { recurrentCall });
        })
            .catch(error => {
            const args = {
                pageNumber: pageProps.pageNumber,
                pageid: pageProps.pageid,
                sort,
                pageSize,
                query,
            };
            this.taxonomyApiResult = null;
            return this.processFailResult(error, args);
        });
    }
    loadTaxonomy(params) {
        return this.taxonomyApi.getData(params);
    }
    loadSpecificTaxonomies(dataFromSearch, umbracoTaxonomies) {
        const loadSpecificTaxonomiesPromises = this.groupFilterTemplates
            .filter(template => template.taxonomyLoader && !template.taxonomyLoader.alreadyProcessed) // only with specific loaders and not processed in current session
            .filter(template => {
            // check taxonomy updated with external mapper
            const taxonomyGroupItem = taxonomy_1.TaxonomyService.getTaxonomyGroupByType(template.type, umbracoTaxonomies);
            // and only if we have relevant facet in data
            return (taxonomyGroupItem &&
                !taxonomyGroupItem.updatedWithExternalMapper &&
                taxonomy_1.TaxonomyService.findFacetByTaxonomyGroupItem(taxonomyGroupItem, dataFromSearch.facets, true));
        })
            .map(template => template.taxonomyLoader.getTaxonomy(umbracoTaxonomies));
        if (loadSpecificTaxonomiesPromises.length) {
            return Promise.all(loadSpecificTaxonomiesPromises).then(specificTaxonomies => {
                specificTaxonomies = specificTaxonomies.filter(value => value);
                if (specificTaxonomies.length) {
                    // we have changed something so we need to update storage
                    this.taxonomyApi.updateStorage(umbracoTaxonomies);
                }
                return [dataFromSearch, umbracoTaxonomies];
            });
        }
        else {
            return [dataFromSearch, umbracoTaxonomies];
        }
    }
    // To load data please override in specific classes
    loadData(params, classReference = null) {
        // classReference must be specified in child classes
        return this.searchApi.doSearch(params, classReference);
    }
    // To apply custom rules please override in specific classes
    buildSearchApiParams(_params) {
        throw new Error('Must be implemented in specific data services');
    }
    // To apply custom rules please override in specific classes
    buildTaxonomyApiParams(pageId) {
        // return result
        return {
            culture: this.config.culture,
            pageId,
            taxonomyAliases: '',
        };
    }
    combineUIParameters(filters, pageNumber, pageId, sort, pageSize, query, isInitialSearch) {
        const result = {
            filters,
            page: pageNumber + 1,
            pageId,
            pageSize,
            query,
            sort,
            isInitialSearch,
        };
        if (!result.page) {
            delete result.page;
        }
        if (!result.filters) {
            delete result.filters;
        }
        if (!result.sort) {
            delete result.sort;
        }
        if (result.pageSize == null) {
            delete result.pageSize;
        }
        if (result.query == null || result.query.length === 0) {
            delete result.query;
        }
        return result;
    }
    processSuccessResult(data, taxonomies, searchApiParams) {
        const result = {};
        result.pageSize = this.getPageSize(data);
        result.totalItems = this.getTotalItems(data);
        result.items = this.convertDocumentsToTiles(data.documentList);
        result.filters = this.convertFacetsToFilters(data.facets, taxonomies, searchApiParams);
        result.filters = this.addExtraTitleToDcsItem(result.filters, taxonomies);
        result.message = null;
        result.informationMessage = null;
        this.applyStatusOnResult(result);
        return result;
    }
    // Dcs - Danfoss Climent Solution segment
    addExtraTitleToDcsItem(filters, taxonomies) {
        const extraTitle = this.getExtraTitleFromDcsTaxonomy(taxonomies);
        return filters.map(filter => {
            if (filter.value !== 'documentationSegment') {
                return filter;
            }
            filter.items.map(item => {
                if (item.value === 'documentationSegment:dcs') {
                    item.extraTitle = extraTitle;
                }
                return item;
            });
            return filter;
        });
    }
    // Dcs - Danfoss Climent Solution segment
    getExtraTitleFromDcsTaxonomy(taxonomies) {
        let extraTitle;
        for (const taxonomy of taxonomies) {
            if (taxonomy.typeAlias !== 'segmentsTaxonomy') {
                continue;
            }
            for (const item of taxonomy.items) {
                if (item.filterValue === 'segments=dcs') {
                    extraTitle = item.documentSearchFilterLabel;
                }
            }
        }
        return extraTitle;
    }
    applyStatusOnResult(result) {
        const itemsNotFound = !result.items || result.items.length < 1;
        if (itemsNotFound) {
            result.zeroResultsMessage = this.config.zeroResultsMessage;
        }
        else {
            result.status = this.createSuccessStatus();
        }
    }
    processFailResult(error, _args) {
        let result = null;
        // Process known error's  else throw error
        if (this.isCommunicationError(error)) {
            result = {
                pageSize: 0,
                status: this.createCommunicationErrorStatus(),
                totalItems: 0,
            };
        }
        else {
            throw new Error(error);
        }
        return result;
    }
    convertDocumentsToTiles(documentList) {
        let result = [];
        if (documentList && documentList.documents) {
            result = documentList.documents.map(document => this.createTileListItem(document));
        }
        return result;
    }
    // To apply custom rules please override in specific classes
    createTileListItem(item) {
        const result = {};
        result.id = item.id;
        return result;
    }
    convertFacetsToFilters(facets, taxonomies, _searchApiParams) {
        return taxonomies
            .filter(this.filterGroupTaxonomy)
            .sort(this.getTaxonomySortFunction())
            .map(item => this.mapTaxonomyToFilterGroupItem(item, facets))
            .filter(model => model && model.items && model.items.length > 0);
    }
    // To apply custom rules please override in specific classes
    filterGroupTaxonomy(_taxonomy) {
        return true;
    }
    getTaxonomySortFunction() {
        const taxonomyPriority = taxonomy_1.TaxonomyService.convertToPriorityByType(this.groupFilterTemplates);
        return (itemA, itemB) => {
            return taxonomyPriority[itemA.typeAlias] - taxonomyPriority[itemB.typeAlias];
        };
    }
    mapTaxonomyToFilterGroupItem(taxonomy, facets) {
        let result = null;
        const facet = taxonomy_1.TaxonomyService.findFacetByTaxonomyGroupItem(taxonomy, facets);
        if (facet) {
            result = this.createFilterGroupItemViewModel(facet, taxonomy);
        }
        return result;
    }
    // To apply custom rules please override in specific classes
    createFilterGroupItemViewModel(facet, taxonomy) {
        const result = {};
        result.value = taxonomy.filterValue;
        const groupsToCollapse = this.groupFilterTemplates.filter(item => item.isCollapsed);
        result.isCollapsed = groupsToCollapse.some((template) => {
            return taxonomy.typeAlias === template.type;
        });
        result.title = taxonomy.title;
        // result.guiType = see FacetsToFilterItemMapper.cs to get more info
        result.items = this.mapTaxonomyToFilterItem(facet.filters, taxonomy.items, taxonomy.typeAlias);
        return result;
    }
    mapTaxonomyToFilterItem(filters, taxonomyItems, _typeAlias) {
        let result = [];
        if (!filters) {
            return result;
        }
        result = filters
            .map(filterItem => {
            const taxonomyItem = taxonomy_1.TaxonomyService.findTaxonomyItemByFilterItem(filterItem, taxonomyItems);
            return taxonomyItem
                ? this.createFilterItemViewModel(filterItem, taxonomyItem, _typeAlias)
                : null;
        })
            .filter(item => item)
            .sort(this.getFilterItemSortFunction());
        return result;
    }
    // To apply custom rules please override in specific classes
    createFilterItemViewModel(filterItem, taxonomyItem, _typeAlias) {
        const result = {};
        result.documentsCount = filterItem.count;
        result.selected = filterItem.selected;
        result.title = taxonomyItem.title;
        result.value = taxonomyItem.filterValue;
        return result;
    }
    // To apply custom sort rules please override in specific classes
    getFilterItemSortFunction() {
        return (0, app_1.getStringCompareFunction)('title');
    }
    calculateGlobalDepth(relatedDepth) {
        if (!relatedDepth) {
            return null;
        }
        const urlDepth = this.config.pagePath.split('/').filter(item => item).length - 1;
        return urlDepth + relatedDepth;
    }
    getPageOffset(page, pageSize) {
        if (!page) {
            return 0;
        }
        return page > 0 ? (page - 1) * pageSize : 0;
    }
    getPageHits(pageSize) {
        return pageSize > 0 ? pageSize : 10;
    }
    getTotalItems(result) {
        return (result.documentList && result.documentList.numberOfHits) || 0;
    }
    getPageSize(result) {
        var _a, _b;
        const pageSizeFromURl = Number(app_1.UrlService.GetQueryValueFromUrl('pageSize'));
        return ((_b = (_a = result.documentList) === null || _a === void 0 ? void 0 : _a.pagination) === null || _b === void 0 ? void 0 : _b.hitsPerPage) || pageSizeFromURl || search_1.PAGE_SIZE;
    }
}
exports.FilterableDataService = FilterableDataService;
